import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import {
  Check, ArrowRight, ArrowLeft, Shield, Users, Building2, Wallet, Landmark,
  ShoppingCart, Store, Boxes, Factory, ShieldCheck, UserCircle, BarChart3,
  HelpCircle, Phone, Mail, CheckCircle2, Sparkles, MessageSquare
} from "lucide-react";
import { MarketingLayout } from "../components/MarketingLayout";
import { Reveal } from "../components/Reveal";
import { useSEO } from "../lib/useSEO";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getPackageSeats } from "../lib/packageSeats";

interface SaasPackage {
  id: number;
  name: string;
  code: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  allowedModules: string[];
  features: string[];
  isPopular: boolean;
  isActive: boolean;
}

const MODULE_DETAILS_MAP: Record<string, { name: string; icon: any; summary: string; capabilities: string[] }> = {
  hr: {
    name: "Core HR & Employee Management",
    icon: Users,
    summary: "Complete employee database, org structure, departments, designations, and digital personnel files.",
    capabilities: ["Employee master directory", "Department & designation hierarchy", "Document repository & identity proofs", "Employee lifecycle (onboarding to exit)"],
  },
  admin: {
    name: "Admin & Facility Management",
    icon: Building2,
    summary: "Manage company assets, conference room bookings, vendor contracts, visitor logs, and facility requests.",
    capabilities: ["Asset tracking & SLM/WDV depreciation", "Software license allocation", "Vendor contract renewal alerts", "Visitor gate pass & badge printing"],
  },
  employee: {
    name: "Employee Self-Service Portal",
    icon: UserCircle,
    summary: "Dedicated portal for employees to mark attendance, apply for leave, view payslips, and submit claims.",
    capabilities: ["Web & geo-fenced attendance marking", "Leave balance & shift requests", "Monthly payslip PDF downloads", "Personal profile & bank details update"],
  },
  payroll: {
    name: "Indian Statutory Payroll",
    icon: Wallet,
    summary: "100% compliant Indian payroll automation handling PF, ESI, Professional Tax, TDS, and Form 16.",
    capabilities: ["One-click monthly salary run", "EPFO ECR file generation", "ESIC portal upload format", "TDS tax calculation & Form 16 Part B"],
  },
  helpdesk: {
    name: "IT & HR Helpdesk Ticketing",
    icon: HelpCircle,
    summary: "Internal ticketing system for IT support, HR queries, maintenance requests, and SLA tracking.",
    capabilities: ["Ticket creation & priority assignment", "SLA breach alerts & escalation", "Resolution comments & audit history", "Departmental queue management"],
  },
  travel: {
    name: "Travel & Expense Reimbursements",
    icon: Wallet,
    summary: "Submit travel requisitions, hotel & flight bookings, daily allowances, and claim reimbursements.",
    capabilities: ["Travel request approval routing", "Receipt photo attachment & OCR", "Policy-enforced daily allowance limits", "Direct Finance approval & payout"],
  },
  finance: {
    name: "Finance & Double-Entry Accounting",
    icon: Landmark,
    summary: "Complete chart of accounts, GST invoicing, Accounts Receivable/Payable, TDS, and financial reports.",
    capabilities: ["GST-compliant tax invoicing", "Chart of Accounts & General Ledger", "Accounts Payable & Vendor Bills", "Bank Reconciliation & Trial Balance"],
  },
  crm: {
    name: "Sales CRM & Lead Pipeline",
    icon: Store,
    summary: "Manage leads, sales opportunities, customer contacts, quotations, and sales team targets.",
    capabilities: ["Lead stage pipeline tracking", "Quotation & estimation builder", "Deal conversion & sales targets", "Activity logs & client follow-ups"],
  },
  procurement: {
    name: "Procurement & Purchase Orders",
    icon: ShoppingCart,
    summary: "Purchase requisitions, RFQs, vendor comparisons, Purchase Orders, and Goods Received Notes (GRN).",
    capabilities: ["Purchase Requisitions (PR) builder", "Purchase Order (PO) approval matrix", "Goods Received Note (GRN) verification", "Vendor bill matching & audit"],
  },
  inventory: {
    name: "Warehouse & Stock Inventory",
    icon: Boxes,
    summary: "Multi-warehouse inventory, stock transfers, batch tracking, reorder levels, and stock valuation.",
    capabilities: ["Multi-warehouse location stock", "Batch number & expiry tracking", "Automatic reorder level alerts", "Weighted average stock valuation"],
  },
  operations: {
    name: "Project & Field Operations",
    icon: Building2,
    summary: "Project milestones, task allocation, timesheets, field service contracts, and resource utilization.",
    capabilities: ["Project task breakdown & Gantt view", "Resource allocation & timesheets", "Field service job cards", "Milestone billing & progress tracking"],
  },
  manufacturing: {
    name: "Manufacturing & Production Orders",
    icon: Factory,
    summary: "Bill of Materials (BOM), work orders, shop floor routing, material requirement planning (MRP).",
    capabilities: ["Multi-level Bill of Materials (BOM)", "Work Order creation & routing", "Raw material allocation & issue", "Production cost & scrap tracking"],
  },
  quality: {
    name: "Quality Assurance & Inspection (NCR/CAPA)",
    icon: ShieldCheck,
    summary: "Material inspection checklists, Non-Conformance Reports (NCR), Corrective and Preventive Actions (CAPA).",
    capabilities: ["Incoming material QC inspection", "In-process quality checks", "NCR logging & root cause analysis", "CAPA resolution workflow"],
  },
  ats: {
    name: "ATS & Applicant Tracking System",
    icon: Users,
    summary: "Job posting, candidate application tracking, interview scheduling, offer letter generation.",
    capabilities: ["Job opening requisition builder", "Resume parsing & stage pipeline", "Interview feedback scorecard", "Automated offer letter issuance"],
  },
  bi: {
    name: "Executive BI & Analytics Scorecards",
    icon: BarChart3,
    summary: "Cross-departmental BI dashboards, executive scorecards, financial ratios, and custom MIS export.",
    capabilities: ["Real-time revenue & expense scorecards", "Employee headcount & turnover metrics", "Procurement spend analytics", "Exportable PDF/Excel MIS reports"],
  },
  service: {
    name: "Customer Field Service & AMC",
    icon: Store,
    summary: "Field service tickets, Annual Maintenance Contracts (AMC), technician dispatch, and service reports.",
    capabilities: ["Customer AMC contract tracking", "Technician dispatch & mobile update", "Service completion sign-off", "Spare parts usage log"],
  },
};

export default function PackageDetails() {
  const [location] = useLocation();
  const searchParams = new URLSearchParams(window.location.search);
  const codeParam = searchParams.get("code") || "STANDARD";

  const [packages, setPackages] = useState<SaasPackage[]>([]);
  const [selectedPkg, setSelectedPkg] = useState<SaasPackage | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetch("/api/public/packages")
      .then((res) => res.json())
      .then((data) => {
        const list: SaasPackage[] = data.packages || [];
        setPackages(list);
        const match = list.find((p) => p.code.toUpperCase() === codeParam.toUpperCase()) || list[0];
        setSelectedPkg(match || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [codeParam]);

  useSEO({
    title: selectedPkg ? `${selectedPkg.name} Package Breakdown — Alea ERP` : "SaaS Package Details — Alea ERP",
    description: "Detailed module explanation, seat limits, and user entitlements for Alea ERP subscription tiers.",
    path: location,
  });

  const seatInfo = getPackageSeats(selectedPkg?.code || "STANDARD");

  return (
    <MarketingLayout>
      {/* Hero Header */}
      <section className="relative overflow-hidden bg-[#0a2d4a] pt-32 pb-20 lg:pt-40">
        <div className="pointer-events-none absolute -top-32 left-1/2 h-96 w-full max-w-[700px] -translate-x-1/2 rounded-full bg-blue-500/20 blur-[120px]" />
        <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <div className="mb-4">
              <Link
                href="/pricing"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-300 hover:text-white transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Pricing Overview
              </Link>
            </div>

            {/* Package selector tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {packages.map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => setSelectedPkg(pkg)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border ${
                    selectedPkg?.id === pkg.id
                      ? "bg-blue-600 text-white border-blue-400 shadow-md scale-105"
                      : "bg-white/10 text-slate-300 border-white/10 hover:bg-white/20"
                  }`}
                >
                  {pkg.name}
                </button>
              ))}
            </div>

            {selectedPkg && (
              <>
                <Badge className="bg-blue-500/20 text-blue-300 border-blue-400/30 mb-3 text-xs uppercase font-mono font-bold px-3 py-1">
                  {selectedPkg.code} TIER SPECIFICATION
                </Badge>
                <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                  {selectedPkg.name}
                </h1>
                <p className="mt-4 text-base text-slate-300 max-w-2xl mx-auto leading-relaxed">
                  {selectedPkg.description}
                </p>

                <div className="mt-6 inline-flex flex-col sm:flex-row items-center gap-2 bg-white/10 border border-white/15 px-6 py-3 rounded-2xl">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-extrabold text-white">
                      ₹{selectedPkg.monthlyPrice.toLocaleString("en-IN")}
                    </span>
                    <span className="text-sm text-slate-300">/ org / month</span>
                  </div>
                  <span className="text-xs text-emerald-400 font-medium">
                    or ₹{selectedPkg.yearlyPrice.toLocaleString("en-IN")}/org/year · {seatInfo.label} included
                  </span>
                </div>
              </>
            )}
          </Reveal>
        </div>
      </section>

      {/* Package Specification Breakdown */}
      {loading || !selectedPkg ? (
        <div className="py-20 text-center text-slate-500">Loading package specifications...</div>
      ) : (
        <section className="bg-slate-50 py-16">
          <div className="mx-auto max-w-5xl px-5 sm:px-8 space-y-12">
            {/* User & Seat Capacity Table */}
            <Reveal>
              <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                  <Users className="h-6 w-6 text-blue-600" />
                  <h2 className="text-xl font-bold text-[#0a2d4a]">
                    User Seats & Role Capacity Breakdown
                  </h2>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {[
                    { label: "Total Platform Users", value: seatInfo.label, desc: "Total employee accounts allowed on the tenant", color: "bg-blue-50 border-blue-100 text-blue-900" },
                    { label: "HR Administrators", value: seatInfo.hrUsers, desc: "Dedicated HR manager & recruiter logins", color: "bg-violet-50 border-violet-100 text-violet-900" },
                    { label: "Finance & Accounts Users", value: seatInfo.financeUsers, desc: "Accountants & CFO role assignments", color: "bg-emerald-50 border-emerald-100 text-emerald-900" },
                    { label: "Department Managers", value: seatInfo.managers, desc: "Approval authority & team lead seats", color: "bg-amber-50 border-amber-100 text-amber-900" },
                    { label: "Cloud File Storage", value: seatInfo.storage, desc: "Encrypted document & invoice storage", color: "bg-indigo-50 border-indigo-100 text-indigo-900" },
                    { label: "Tenant Isolation", value: "Dedicated DB Schema", desc: "Strict RLS multi-tenant data isolation", color: "bg-slate-50 border-slate-200 text-slate-900" },
                  ].map((item) => (
                    <div key={item.label} className={`rounded-xl border p-4 ${item.color}`}>
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{item.label}</div>
                      <div className="mt-1 text-lg font-extrabold">{item.value}</div>
                      <div className="mt-1 text-[11px] text-slate-500">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Included Modules Detailed Explanation */}
            <Reveal>
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-extrabold text-[#0a2d4a]">
                    Included Modules & Operational Capabilities
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Below is the detailed operational breakdown of every module included in the <span className="font-semibold text-slate-800">{selectedPkg.name}</span>.
                  </p>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  {(selectedPkg.allowedModules || []).map((modKey) => {
                    const modInfo = MODULE_DETAILS_MAP[modKey.toLowerCase()] || {
                      name: modKey.toUpperCase() + " Module",
                      icon: CheckCircle2,
                      summary: "Full access to operational panel.",
                      capabilities: ["Module feature access", "Role-based control", "Reporting"],
                    };
                    const Icon = modInfo.icon;
                    return (
                      <div key={modKey} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-3 mb-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 border border-blue-100 shrink-0">
                              <Icon className="h-5 w-5" />
                            </div>
                            <div>
                              <h3 className="text-base font-bold text-[#0a2d4a]">{modInfo.name}</h3>
                              <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px] uppercase font-mono font-bold">
                                Included in {selectedPkg.code}
                              </Badge>
                            </div>
                          </div>
                          <p className="text-xs text-slate-600 leading-relaxed mb-4">
                            {modInfo.summary}
                          </p>

                          <div className="space-y-2 pt-3 border-t border-slate-100">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                              Key Capabilities:
                            </p>
                            <ul className="space-y-1.5">
                              {modInfo.capabilities.map((cap, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-xs text-slate-700">
                                  <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                  <span>{cap}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Reveal>

            {/* Need Help / Contact Us Section */}
            <Reveal>
              <div className="rounded-3xl border border-blue-200 bg-gradient-to-br from-[#0a2d4a] to-[#0f436d] p-8 text-white shadow-xl relative overflow-hidden">
                <div className="pointer-events-none absolute -right-12 -bottom-12 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" />
                <div className="relative z-10 grid gap-6 md:grid-cols-[2fr_1fr] items-center">
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/30 text-blue-200 border border-blue-400/30 mb-3">
                      <MessageSquare className="w-3.5 h-3.5 text-amber-400" />
                      <span>Have Questions or Need a Custom Plan?</span>
                    </div>
                    <h3 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
                      Still unsure which package fits your business best?
                    </h3>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                      Our enterprise sales team can clarify module limits, adjust user capacity, or arrange a tailored live product demonstration.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-300">
                      <div className="flex items-center gap-1.5">
                        <Phone className="h-4 w-4 text-blue-400" />
                        <span>+91 96103 65064</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Mail className="h-4 w-4 text-blue-400" />
                        <span>ash@aleaitsolutions.com</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 justify-center">
                    <Link
                      href="/contact"
                      className="w-full rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 text-center text-sm transition-all shadow-md flex items-center justify-center gap-2"
                    >
                      <span>Contact Us for Clarification</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link
                      href="/demo-access"
                      className="w-full rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 text-white font-semibold py-3 text-center text-sm transition-all"
                    >
                      Schedule Product Demo
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}
    </MarketingLayout>
  );
}
